import React from "react";
import { Button } from "../../../ui/button";
import { Copy, Scissors, Clipboard, Trash2 } from "lucide-react";
import { ContextMenuState } from "../hooks/hooks";
import { ClipboardData } from "../utils/types";
import { cn } from "../../../../lib/utils";

interface ContextMenuProps {
  contextMenu: ContextMenuState;
  clipboard: ClipboardData;
  onCopyNode: (nodeId: string) => void;
  onCopyBranch: (nodeId: string) => void;
  onPasteNode: (nodeId: string) => void;
  onPasteBranch: (nodeId: string) => void;
  onDeleteNode: (nodeId: string) => void;
  onDeleteBranch: (nodeId: string) => void;
  onHide: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  contextMenu,
  clipboard,
  onCopyNode,
  onCopyBranch,
  onPasteNode,
  onPasteBranch,
  onDeleteNode,
  onDeleteBranch,
  onHide,
}: ContextMenuProps) => {
  if (!contextMenu.visible || !contextMenu.nodeId) {
    return null;
  }

  return (
    <div
      data-context-menu
              className="fixed bg-background border border-border rounded-md shadow-lg z-40 py-1 min-w-[160px]"
      style={{
        top: contextMenu.y,
        left: contextMenu.x,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start gap-2 h-8 px-3 rounded-none hover:bg-accent"
        onClick={() => {
          onCopyNode(contextMenu.nodeId!);
          onHide();
        }}
      >
        <Copy className="w-4 h-4" />
        Copy Node
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start gap-2 h-8 px-3 rounded-none hover:bg-accent"
        onClick={() => {
          onCopyBranch(contextMenu.nodeId!);
          onHide();
        }}
      >
        <Scissors className="w-4 h-4" />
        Copy Branch
      </Button>
      
      {clipboard.type === "node" && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 h-8 px-3 rounded-none hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => {
            onPasteNode(contextMenu.nodeId!);
            onHide();
          }}
          disabled={!clipboard.sourceNode}
        >
          <Clipboard className="w-4 h-4" />
          Paste Node
        </Button>
      )}
      
      {clipboard.type === "branch" && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 h-8 px-3 rounded-none hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => {
            onPasteBranch(contextMenu.nodeId!);
            onHide();
          }}
          disabled={!clipboard.sourceBranch}
        >
          <Clipboard className="w-4 h-4" />
          Paste Branch
        </Button>
      )}
      
      <div className="h-px bg-border mx-1 my-1" />
      
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start gap-2 h-8 px-3 rounded-none text-destructive hover:bg-destructive/10"
        onClick={() => {
          onDeleteNode(contextMenu.nodeId!);
          onHide();
        }}
      >
        <Trash2 className="w-4 h-4" />
        Delete Node
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start gap-2 h-8 px-3 rounded-none text-destructive hover:bg-destructive/10"
        onClick={() => {
          onDeleteBranch(contextMenu.nodeId!);
          onHide();
        }}
      >
        <Trash2 className="w-4 h-4" />
        Delete Branch
      </Button>
    </div>
  );
};

export default ContextMenu; 